import Link, { type LinkProps } from '@components/atoms/Link'

import type { ComponentDoc, PropDocs } from '../types'

type LinkOwnProps = Pick<
  LinkProps,
  | 'href'
  | 'children'
  | 'as'
  | 'rank'
  | 'color'
  | 'size'
  | 'leadingIcon'
  | 'trailingIcon'
  | 'linkBackground'
  | 'disabled'
  | 'ariaLabel'
  | 'only'
  | 'navigate'
  | 'className'
  | 'dataCompId'
  | 'ref'
>

const props: PropDocs<LinkOwnProps> = {
  href: { type: 'string', required: true, description: 'Where it goes. An empty href renders without an href.' },
  children: { type: 'ReactNode', required: true, description: 'The link text.' },
  as: {
    type: "'a' | 'inertiaLink' | 'modalLink'",
    default: "'a'",
    description: 'How it navigates. See "Which as to use" below.',
  },
  rank: {
    type: "'primary' | 'secondary' | 'link'",
    default: "'primary'",
    description: 'Visual prominence, the same as Button.',
  },
  color: {
    type: "'primary' | 'danger' | 'success' | 'warning'",
    default: "'primary'",
    description: 'Colour family, the same as Button.',
  },
  size: { type: "'sm' | 'md' | 'xl' | '2xl'", default: "'md'", description: 'Padding and type scale.' },
  leadingIcon: { type: 'IconTypes', description: 'Sprite glyph before the text.' },
  trailingIcon: { type: 'IconTypes', description: 'Sprite glyph after the text. Use external-link for other sites.' },
  linkBackground: {
    type: 'boolean',
    default: 'true',
    description: 'For rank="link": whether hover paints a tinted background.',
  },
  disabled: { type: 'boolean', description: 'Drops the href and takes the link out of the tab order.' },
  ariaLabel: { type: 'string', description: 'Accessible name, when the text alone does not say where it goes.' },
  only: { type: 'string[]', description: 'For as="inertiaLink": reload only these page props.' },
  navigate: {
    type: 'boolean',
    default: 'true',
    description: 'For as="modalLink": whether opening the modal updates the URL.',
  },
  className: { type: 'string', description: 'Extra classes, merged with cn so conflicts resolve to yours.' },
  dataCompId: { type: 'string', description: 'Renders data-compid="<id>-link".' },
  ref: { type: 'Ref<HTMLAnchorElement>', description: 'Forwarded to the anchor.' },
}

const doc: ComponentDoc<LinkOwnProps> = {
  slug: 'link',
  name: 'Link',
  tier: 'atom',
  description: 'A link styled like a Button. Use it when the click goes to another page.',
  importStatement: "import { Link } from '@atoms'",
  sourcePath: 'app/frontend/components/atoms/Link.tsx',
  component: Link,
  props,
  examples: [
    {
      title: 'Ranks',
      variants: [
        { href: '#', rank: 'primary', children: 'Primary' },
        { href: '#', rank: 'secondary', children: 'Secondary' },
        { href: '#', rank: 'link', children: 'Link' },
        { href: '#', rank: 'link', linkBackground: false, children: 'Link, no hover background' },
      ],
    },
    {
      title: 'With icons',
      note: 'A link to another site gets the external-link icon after the text.',
      variants: [
        { href: '#', rank: 'secondary', leadingIcon: 'arrow-narrow-left', children: 'All groups' },
        { href: 'https://example.com', rank: 'link', trailingIcon: 'external-link', children: 'Help centre' },
      ],
    },
    {
      title: 'Disabled',
      variants: [
        { href: '#', disabled: true, children: 'Disabled' },
        { href: '#', rank: 'secondary', disabled: true, children: 'Disabled secondary' },
      ],
    },
    {
      title: 'Which as to use',
      note: 'as="inertiaLink" when both pages are Inertia pages. as="modalLink" to open an Inertia modal. Leave the default a when either page is legacy (v1), or the link is a download or another site. A plain a between two Inertia pages reloads the whole app.',
      // ModalLink ignores onClick, so the modal example is code only.
      render: () => (
        <div className="tw:flex tw:flex-wrap tw:gap-3">
          <Link href="#" as="inertiaLink" rank="secondary" onClick={(event) => event.preventDefault()}>
            Inertia page
          </Link>
          <Link href="#" rank="secondary" onClick={(event) => event.preventDefault()}>
            Legacy page
          </Link>
        </div>
      ),
      code: `<Link href={groupPath} as="inertiaLink">Inertia page</Link>
<Link href={newSupporterPath} as="modalLink">Open modal</Link>
<Link href={legacyPath}>Legacy page</Link>`,
    },
  ],
  usage: {
    do: [
      'Use Link when the click goes somewhere. Use Button when it does something on this page.',
      'Check both ends of the link before you pick as. Both must be Inertia pages for inertiaLink.',
      'Write text that says where it goes, such as "All groups".',
    ],
    dont: [
      'Use a Button with router.visit to go to a page. Use a Link so people can open it in a new tab.',
      'Use a plain a between two Inertia pages. It reloads the whole app.',
      'Disable a link without saying why nearby.',
    ],
  },
  a11y: [
    'Renders an anchor, so it opens in a new tab and shows its URL on hover.',
    'A disabled link has no href, carries aria-disabled and is out of the tab order.',
    'Keyboard users never reach a disabled link. Put the reason in visible text, not only on the link.',
    'It shows a focus ring when reached by keyboard.',
  ],
}

export default doc
